import { NAME_OWNER_ATTRIBUTE, newNameSourceId } from './host-name-source';

/**
 * The parent id this helper last pointed each button at. It is how the helper tells a link it
 * wrote itself, which it keeps up to date, from one a template or a helper wrote, which it must
 * never touch. Keyed by the button, so an element that goes away is dropped.
 */
const linkWrittenByButton = new WeakMap<Element, string>();

/**
 * Gives a `<button cs-capture-cover>` written straight into a template an accessible name taken
 * from its parent element, by pointing its `aria-labelledby` at that parent.
 *
 * A tappable row is usually drawn as an empty capture-cover button with the row's words beside
 * it: `<div class="menu-item"><button cs-capture-cover …></button><span>Kilograms</span></div>`.
 * The button's name is computed from its own empty contents, so a screen reader says only
 * "button". The row's words sit in the button's parent, so pointing at the parent reads them out.
 * This is the rule `syncButtonNameFromHost` applies inside a component, with the parent standing
 * in for the host. `CsCaptureCoverDirective` calls it for every capture-cover button, so no call
 * site has to ask for it.
 *
 * It stays out of the way of every other name:
 * - a button a naming helper has claimed (`NAME_OWNER_ATTRIBUTE`) is that helper's to name;
 * - an `aria-label` on the button wins, as it does in both helpers;
 * - an `aria-labelledby` this helper did not write came from a template or a helper, and is kept;
 * - a parent with no text of its own would give an empty name, so it is not linked at all.
 * In every one of those cases it removes only a link it wrote itself, and never anybody else's.
 *
 * Call it from `ngAfterViewChecked`, for the same reason the host version says: the parent's id
 * can change after the first pass, and a link to an old id names nothing.
 */
export function syncButtonNameFromParent(button: HTMLElement): void {
  const written = linkWrittenByButton.get(button);
  const current = button.getAttribute('aria-labelledby');
  // if a helper names this button, or somebody else linked it, it is not ours to name
  if (button.hasAttribute(NAME_OWNER_ATTRIBUTE) || (current !== null && current !== written)) {
    linkWrittenByButton.delete(button);
    return;
  }
  const parent = button.parentElement;
  const hasExplicitName = !!button.getAttribute('aria-label');
  const parentHasText = !!parent && !!(parent.textContent || '').trim();
  if (hasExplicitName || !parentHasText) {
    // if we linked it on an earlier pass, that link no longer names anything useful
    if (current !== null) {
      button.removeAttribute('aria-labelledby');
    }
    linkWrittenByButton.delete(button);
    return;
  }
  // if the parent has no id yet, give it one nothing else uses
  if (!parent.id) {
    parent.id = newNameSourceId();
  }
  if (current !== parent.id) {
    button.setAttribute('aria-labelledby', parent.id);
  }
  linkWrittenByButton.set(button, parent.id);
}
